import { useState } from 'react';

interface DownloadOptions {
  newTab?: boolean;
  forceDownload?: boolean;
  filename?: string;
}

export const useCompatibleDownload = () => {
  const [isLoading, setIsLoading] = useState(false);

  const download = async (getUrlFn: () => Promise<string>, options: DownloadOptions = {}) => {
    const { newTab = true, forceDownload = false, filename } = options;

    setIsLoading(true);
    let newWindow: Window | null = null;

    try {
      const url = await getUrlFn();
      const isSafariIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

      // Helper function for window-based approach
      const openWithWindow = (): Window | null => {
        if (newTab) {
          const win = window.open(
            'data:text/html,<html><body><div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:Arial,sans-serif;"><h2>Loading...</h2></div></body></html>',
            '_blank',
          );

          if (win && !win.closed) {
            win.location.href = url;
          } else {
            window.location.href = url;
          }
          return win;
        } else {
          window.location.href = url;
          return null;
        }
      };

      // Helper function for anchor-based approach
      const openWithAnchor = () => {
        const a = document.createElement('a');
        a.href = url;
        if (forceDownload) a.download = filename || '';
        if (newTab) a.target = '_blank';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      };

      if (forceDownload && isSafariIOS) {
        // Safari iOS: use window method for downloads
        newWindow = openWithWindow();
      } else if (forceDownload || (!newTab && !forceDownload)) {
        // Other browsers with download, or same-tab navigation
        openWithAnchor();
      } else {
        // New tab without download
        newWindow = openWithWindow();
      }
    } catch (error) {
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { download, isLoading };
};
